import { useState, useMemo, useEffect } from 'react'
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import ViolationRow from './ViolationRow'
import type { ComplianceViolation, PolicyCriticality, ViolationStatus } from '@/types'

interface ViolationsTableProps {
  violations: ComplianceViolation[]
  isLoading?: boolean
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  /** Default criticality filter. Defaults to 'all' */
  defaultCriticalityFilter?: PolicyCriticality | 'all'
  /** Default status filter. Defaults to 'all' */
  defaultStatusFilter?: ViolationStatus | 'all'
  /** Framework code for contextual framework reference display (e.g., "EIDSCA", "SCUBA") */
  frameworkCode?: string
  /** Policy code to focus: scrolls into view, expands the row, and clears filters that would hide it. */
  focusPolicyCode?: string | null
  /** Called once the focus has been consumed (scroll initiated). */
  onFocusConsumed?: () => void
}

type SortField = 'policyCode' | 'policyTitle' | 'criticality' | 'category' | 'status' | 'detectedAt'
type SortDirection = 'asc' | 'desc'
type ViewMode = 'failing' | 'passing' | 'all'

const isPassing = (v: ComplianceViolation) => v.status === 'Pass'

// Editorial palette tokens — paper, navy text, cream hover, severity tints.
// Solid critical badge (#A03434) matches the §06 violations sub-nav in BestPracticeAnalyzerTab.
const SEVERITY_PILL: Record<PolicyCriticality, { container: string; dot: string }> = {
  SHALL: { container: 'bg-[#A03434] text-white', dot: 'bg-white/80' },
  SHOULD: { container: 'bg-[#FDF3DF] text-[#8C6B22]', dot: 'bg-[#8C6B22]' },
  MAY: { container: 'bg-[#E8F0F6] text-[#4F86A8]', dot: 'bg-[#4F86A8]' },
}

// In Passing mode every severity reads the same green chip — pass/fail is the only signal that matters.
const PASSING_PILL = { container: 'bg-[#E2F1E8] text-[#1F6B43]', dot: 'bg-[#1F6B43]' }

// Inputs share one editorial chrome — white surface, rule border, navy focus.
const FIELD_BASE =
  'rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[13px] text-[color:var(--color-navy,#212938)] placeholder-[color:var(--color-faint,#9CA3AF)] focus:outline-none focus:border-[color:var(--color-navy,#212938)]'

/**
 * Filterable table of compliance policies with sorting and search.
 * A view-mode toggle switches between Failing (default), Passing, and All rows.
 * Columns: Policy Code, Title, Criticality, Framework, Status.
 */
export default function ViolationsTable({
  violations,
  isLoading = false,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
  defaultCriticalityFilter = 'all',
  defaultStatusFilter = 'all',
  frameworkCode,
  focusPolicyCode,
  onFocusConsumed,
}: ViolationsTableProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('failing')
  const [searchQuery, setSearchQuery] = useState('')
  const [criticalityFilter, setCriticalityFilter] = useState<PolicyCriticality | 'all'>(defaultCriticalityFilter)
  const [statusFilter, setStatusFilter] = useState<ViolationStatus | 'all'>(defaultStatusFilter)
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('criticality')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // When a focus is requested, drop view-mode and column filters so the target row
  // is guaranteed to be visible regardless of the user's prior filter state.
  useEffect(() => {
    if (!focusPolicyCode) return
    setViewMode('all')
    setSearchQuery('')
    setCriticalityFilter('all')
    setStatusFilter('all')
    setCategoryFilter('all')
  }, [focusPolicyCode])

  useEffect(() => {
    if (!focusPolicyCode) return
    // Wait for tab content to render before scrolling so the target row exists in the DOM.
    const rafId = requestAnimationFrame(() => {
      const el = document.getElementById(`violation-${focusPolicyCode}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      onFocusConsumed?.()
    })
    return () => cancelAnimationFrame(rafId)
  }, [focusPolicyCode, onFocusConsumed])

  // Extract unique categories for filter dropdown
  const categories = useMemo(() => {
    const uniqueCategories = new Set(violations.map((v) => v.category))
    return Array.from(uniqueCategories).sort()
  }, [violations])

  // Apply every filter EXCEPT viewMode; used for per-mode counts in the toggle.
  const withoutModeFilter = useMemo(() => {
    let filtered = violations

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (v) =>
          v.policyCode.toLowerCase().includes(query) ||
          v.policyTitle.toLowerCase().includes(query) ||
          v.message.toLowerCase().includes(query) ||
          v.category.toLowerCase().includes(query) ||
          v.product.toLowerCase().includes(query)
      )
    }

    if (criticalityFilter !== 'all') {
      filtered = filtered.filter((v) => v.criticality === criticalityFilter)
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((v) => v.status === statusFilter)
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter((v) => v.category === categoryFilter)
    }

    return filtered
  }, [violations, searchQuery, criticalityFilter, statusFilter, categoryFilter])

  // Toggle counts reflect what the user would see in each mode, honouring other filters.
  const modeCounts = useMemo(() => {
    let failing = 0
    let passing = 0
    for (const v of withoutModeFilter) {
      if (isPassing(v)) passing++
      else failing++
    }
    return { failing, passing, all: withoutModeFilter.length }
  }, [withoutModeFilter])

  // Filter and sort violations
  const filteredViolations = useMemo(() => {
    let filtered = withoutModeFilter

    // View-mode filter applies before sort. 'all' shows everything.
    if (viewMode === 'failing') {
      filtered = filtered.filter((v) => !isPassing(v))
    } else if (viewMode === 'passing') {
      filtered = filtered.filter(isPassing)
    }

    // Sort
    const criticalityOrder: Record<PolicyCriticality, number> = { SHALL: 3, SHOULD: 2, MAY: 1 }
    const statusOrder: Record<string, number> = { Open: 3, Waived: 2, Resolved: 1 }

    filtered = [...filtered].sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'policyCode':
          comparison = a.policyCode.localeCompare(b.policyCode)
          break
        case 'policyTitle':
          comparison = a.policyTitle.localeCompare(b.policyTitle)
          break
        case 'criticality':
          comparison = criticalityOrder[a.criticality] - criticalityOrder[b.criticality]
          break
        case 'category':
          comparison = a.category.localeCompare(b.category)
          break
        case 'status':
          comparison = statusOrder[a.status] - statusOrder[b.status]
          break
        case 'detectedAt':
          comparison = new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime()
          break
      }

      return sortDirection === 'desc' ? -comparison : comparison
    })

    return filtered
  }, [withoutModeFilter, viewMode, sortField, sortDirection])

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-3 w-3" />
    ) : (
      <ChevronDownIcon className="h-3 w-3" />
    )
  }

  // Chip counts reflect the active view: Open-only for failing, Pass-only for passing,
  // and the full universe per criticality for all.
  const criticalityCounts = useMemo(() => {
    const predicate: (v: ComplianceViolation) => boolean =
      viewMode === 'passing'
        ? isPassing
        : viewMode === 'failing'
          ? (v) => v.status === 'Open'
          : () => true
    return {
      SHALL: violations.filter((v) => v.criticality === 'SHALL' && predicate(v)).length,
      SHOULD: violations.filter((v) => v.criticality === 'SHOULD' && predicate(v)).length,
      MAY: violations.filter((v) => v.criticality === 'MAY' && predicate(v)).length,
    }
  }, [violations, viewMode])

  // In Passing mode every severity uses the same green chip; otherwise red/amber/blue per severity.
  const chipPillFor = (level: PolicyCriticality) =>
    viewMode === 'passing' ? PASSING_PILL : SEVERITY_PILL[level]
  const chipLabel: Record<PolicyCriticality, string> =
    viewMode === 'passing'
      ? {
          SHALL: 'Critical (SHALL) in place',
          SHOULD: 'High (SHOULD) in place',
          MAY: 'Medium (MAY) in place',
        }
      : {
          SHALL: 'Critical (SHALL)',
          SHOULD: 'High (SHOULD)',
          MAY: 'Medium (MAY)',
        }

  // Editorial segmented toggle: active = solid navy, inactive = white with cream hover.
  // Matches the chip pattern at BestPracticeAnalyzerTab.tsx:1244-1257.
  const tabClasses = (mode: ViewMode) => {
    const active = viewMode === mode
    return active
      ? 'border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] text-[color:var(--color-paper,#fff)]'
      : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]'
  }

  const rowNoun = viewMode === 'passing' ? 'passing controls' : viewMode === 'all' ? 'policies' : 'violations'
  const emptyCopy = violations.length === 0
    ? 'No compliance data available yet.'
    : viewMode === 'passing'
      ? 'No passing controls match the current filters.'
      : viewMode === 'failing'
        ? 'No violations match the current filters.'
        : 'No policies match the current filters.'

  if (isLoading) {
    return (
      <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-full rounded-[4px] bg-[color:var(--color-cream,#FBF8F2)]" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 rounded-[4px] bg-[color:var(--color-cream,#FBF8F2)]" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* View-mode toggle + criticality summary chips */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em]">
          {(['failing', 'passing', 'all'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setViewMode(mode)
                // Status filter applies to lifecycle states (Open/Waived/Resolved) which
                // don't exist on Pass rows — reset it so Passing mode never silently hides data.
                if (mode === 'passing') setStatusFilter('all')
              }}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors focus:outline-none ${tabClasses(mode)}`}
            >
              {mode === 'failing' ? 'Failing' : mode === 'passing' ? 'Passing' : 'All'}
              <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums">
                {modeCounts[mode]}
              </span>
            </button>
          ))}
        </div>
        {(['SHALL', 'SHOULD', 'MAY'] as const).map((level) => {
          if (criticalityCounts[level] === 0) return null
          const pill = chipPillFor(level)
          return (
            <div
              key={level}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${pill.container}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} />
              <span className="tabular-nums">
                {criticalityCounts[level]} {chipLabel[level]}
              </span>
            </div>
          )
        })}
      </div>

      {/* Filters row */}
      <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative min-w-[220px] flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-faint,#9CA3AF)]" />
            <input
              type="text"
              placeholder={`Search ${rowNoun}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full py-2 pl-9 pr-3 ${FIELD_BASE}`}
            />
          </div>

          {/* Criticality filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-[color:var(--color-faint,#9CA3AF)]" />
            <select
              value={criticalityFilter}
              onChange={(e) => setCriticalityFilter(e.target.value as PolicyCriticality | 'all')}
              className={`px-3 py-2 ${FIELD_BASE}`}
            >
              <option value="all">All Criticality</option>
              <option value="SHALL">SHALL (Critical)</option>
              <option value="SHOULD">SHOULD (High)</option>
              <option value="MAY">MAY (Medium)</option>
            </select>
          </div>

          {/* Status filter — only meaningful for Failing/All. All Pass rows share one status. */}
          {viewMode !== 'passing' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ViolationStatus | 'all')}
              className={`px-3 py-2 ${FIELD_BASE}`}
            >
              <option value="all">All Status</option>
              <option value="Open">Open</option>
              <option value="Waived">Waived</option>
              <option value="Resolved">Resolved</option>
            </select>
          )}

          {/* Category filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={`px-3 py-2 ${FIELD_BASE}`}
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Sort controls */}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[color:var(--color-rule,#E6E1D5)] pt-3 text-[10px] font-bold uppercase tracking-[0.16em]">
          <span className="text-[color:var(--color-faint,#9CA3AF)]">Sort by</span>
          {[
            { field: 'criticality', label: 'Criticality' },
            { field: 'policyCode', label: 'Policy Code' },
            { field: 'category', label: 'Category' },
            { field: 'status', label: 'Status' },
            { field: 'detectedAt', label: 'Date' },
          ].map(({ field, label }) => {
            const active = sortField === field
            return (
              <button
                key={field}
                onClick={() => handleSort(field as SortField)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 transition-colors focus:outline-none ${
                  active
                    ? 'border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] text-[color:var(--color-paper,#fff)]'
                    : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]'
                }`}
              >
                {label}
                <SortIndicator field={field as SortField} />
              </button>
            )
          })}
        </div>
      </div>

      {/* Results count */}
      <div className="text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
        Showing {filteredViolations.length} of {modeCounts[viewMode]} {rowNoun}
      </div>

      {/* Policy list */}
      <div className="space-y-3">
        {filteredViolations.length === 0 ? (
          <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-8 text-center">
            <p className="italic text-[color:var(--color-mute,#6B7280)]">{emptyCopy}</p>
          </div>
        ) : (
          filteredViolations.map((violation) => (
            <ViolationRow
              key={violation.id}
              violation={violation}
              onAddWaiver={onAddWaiver}
              onEditWaiver={onEditWaiver}
              onDeleteWaiver={onDeleteWaiver}
              frameworkCode={frameworkCode}
              id={`violation-${violation.policyCode}`}
              defaultExpanded={violation.policyCode === focusPolicyCode}
            />
          ))
        )}
      </div>
    </div>
  )
}
